import { ImageEntry } from '@/lib/parsers/images';
import { Sprinkles } from '@/theme/sprinkles.css';
import { mergePropsClassName } from '@/lib/helpers';
import NextImage, { ImageProps as NextImageProps } from 'next/image';
import Shim, { ShimProps } from '../Shim';
import * as styles from './Image.css';

export type ImageProps<T extends TagName = 'img'> = PropsOverride<
  NextImageProps,
  Partial<ImageEntry> & {
    ShimProps?: ShimProps<T>;
    ratio?: Sprinkles['ratio'];
    rounded?: boolean;
    caption?: string | null;
  } & styles.ImageVariants
>;

const Image = <T extends TagName>(props: ImageProps<T>) => {
  const {
    ratio,
    ShimProps: maybeShimProps,
    fixedRatio: maybeFixedRatio,
    title,
    contain,
    rounded,
    round,
    mimeType: _mimeType,
    noCrop: _noCrop,
    src,
    sizes,
    ...rest
  } = props;

  /**
   * Create the Shim
   */
  const ShimProps: ShimProps<T> | undefined =
    maybeShimProps || ratio || rounded || round
      ? (mergePropsClassName(
          { ratio, round, rounded, ...maybeShimProps },
          styles.shim
        ) as ShimProps<T>)
      : undefined;

  const fixedRatio = maybeFixedRatio ?? !!ShimProps;
  const responsiveSizes =
    sizes ??
    (typeof rest.width === 'number'
      ? `(max-width: ${rest.width}px) 100vw, ${rest.width}px`
      : '100vw');

  const img = !src ? (
    <></>
  ) : (
    <NextImage
      alt=""
      {...{
        ...mergePropsClassName(
          { ...rest, sizes: responsiveSizes },
          styles.root({
            fixedRatio,
            contain,
          })
        ),
        src,
      }}
    />
  );

  if (ShimProps) return <Shim {...ShimProps}>{img}</Shim>;

  return img;
};

Image.styles = styles;

export default Image;
